<script setup lang="ts">
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinConfigurationType } from "~/modules/dtwin/models/DtwinConfigurationType.enum";
import type { DtwinFormOperationListColumnConfig } from "~/modules/dtwin/models/DtwinFormOperationListColumnConfig.type";
import { DtwinFormOperationListColumns } from "~/modules/dtwin/models/DtwinFormOperationListColumns.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";

const props = defineProps<{
  organizationCode: number;
  dtwin: Dtwin;
  dtwinModel: DtwinModel;
  dtwinOperationModel: OperationModel;
  reload: boolean;
}>();

const emit = defineEmits(["showOperationForm"]);

const { t } = useI18n();

const baseComponentRef = ref<
  (ComponentPublicInstance & { reload: () => Promise<void> | void }) | undefined
>();

const isSelfSupportingConfiguration = computed(() => {
  return (
    props.dtwin?.configurationType === DtwinConfigurationType.SELF_SUPPORTING
  );
});

// Column configuration according to the configuration type
const columnConfig = computed<DtwinFormOperationListColumnConfig[]>(() => {
  const baseColumns: DtwinFormOperationListColumnConfig[] = [];

  // Custom columns for versions
  if (isSelfSupportingConfiguration.value) {
    baseColumns.push({
      key: "selfSupportingVersion",
      prop: "selfSupportingVersion",
      label: t("operation.configuration.selfSupportingVersion"),
      slot: "selfSupportingVersion",
      minWidth: "12rem",
    });
  } else {
    baseColumns.push(
      {
        key: "globalVersion",
        prop: "globalVersion",
        label: t("operation.configuration.globalVersion"),
        slot: "globalVersion",
        minWidth: "12rem",
      },
      {
        key: "specificVersion",
        prop: "specificVersion",
        label: t("operation.configuration.specificVersion"),
        slot: "specificVersion",
        minWidth: "12rem",
      },
    );
  }

  // Add standard columns
  baseColumns.push(
    DtwinFormOperationListColumns.CREATED_AT,
    DtwinFormOperationListColumns.LAUNCHED_AT,
    DtwinFormOperationListColumns.MODIFIED_AT,
    DtwinFormOperationListColumns.STATUS,
  );

  return baseColumns;
});

function showOperationForm(operation: DtwinOperation) {
  emit("showOperationForm", operation);
}
</script>

<template>
  <dtwin-form-operation
    ref="baseComponentRef"
    :organization-code="organizationCode"
    :dtwin="dtwin"
    :dtwin-model="dtwinModel"
    :dtwin-operation-model="dtwinOperationModel"
    :operation-type="OperationModelType.CONFIG"
    :columns="columnConfig"
    :reload="reload"
    @edit="showOperationForm"
  >
    <!-- Slot for selfSupportingVersion -->
    <template #column-selfSupportingVersion="{ row }">
      {{ row.customInputParameters?.version || "" }}
    </template>

    <!-- Slot for globalVersion -->
    <template #column-globalVersion="{ row }">
      {{ row.customInputParameters?.globalVersion || "" }}
    </template>

    <!-- Slot for specificVersion -->
    <template #column-specificVersion="{ row }">
      {{ row.customInputParameters?.specificVersion || "" }}
    </template>
  </dtwin-form-operation>
</template>
